// App activity (brief 1A-7, PLAN.md 6 Business › Usage): how long office staff were active in the
// app, by area and New York day. Only the area and the minute are kept; never what was typed or
// shown, no screenshots, no keystrokes. Pure: the time is handed in.

import { DEFAULT_TIME_ZONE, zonedDay } from "./week";

/** The shell sends one heartbeat a minute while the tab is visible and the person is active. */
export const BEAT_EVERY_MS = 60_000;
/** Active means a key, click, touch or scroll in the last minute. */
export const ACTIVE_WITHIN_MS = 60_000;
/** Beats closer together than this add nothing, so a person never gets more than a minute a minute. */
export const MIN_BEAT_GAP_MS = 50_000;

/** "home" is the Home screen; every other area is a section of the shell (staffing, settings...). */
export const HOME_AREA = "home";

/** Whether the shell should send a heartbeat now: the tab is visible and was used in the last minute. */
export function shouldBeat(state: {
  visible: boolean;
  lastInputAt: number | null;
  now: number;
}): boolean {
  if (!state.visible || state.lastInputAt === null) return false;
  return state.now - state.lastInputAt <= ACTIVE_WITHIN_MS;
}

/** The only thing a heartbeat carries. */
export interface Heartbeat {
  area: string;
}

/** What the shell sends for the screen it shows: the area, nothing else. */
export function heartbeatFor(path: string): Heartbeat {
  const first = path.split("/").filter(Boolean)[0];
  return { area: first && /^[a-z]{1,32}$/.test(first) ? first : HOME_AREA };
}

/**
 * Reads a heartbeat from a request body. It must be exactly {area} and the area one the person
 * can open (Home, or a section of their menus); anything more is refused, so nothing about what
 * was typed or shown can be sent along.
 */
export function readHeartbeat(
  input: unknown,
  areas: readonly string[],
): { ok: true; area: string } | { ok: false; error: string } {
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return { ok: false, error: "send the area" };
  const keys = Object.keys(input);
  if (keys.length !== 1 || keys[0] !== "area")
    return { ok: false, error: "a heartbeat carries the area and nothing else" };
  const area = (input as Heartbeat).area;
  if (typeof area !== "string" || !(area === HOME_AREA || areas.includes(area)))
    return { ok: false, error: "not an area you can open" };
  return { ok: true, area };
}

/** The day a minute counts toward: the New York date. */
export const usageDay = (now: Date, zone = DEFAULT_TIME_ZONE): string => zonedDay(now, zone);

/** Whether a beat counts: the person's last counted beat (any area) was long enough ago. */
export function beatCounts(lastBeatAt: Date | null, now: Date): boolean {
  return lastBeatAt === null || now.getTime() - lastBeatAt.getTime() >= MIN_BEAT_GAP_MS;
}
